import React, { useEffect, useState } from 'react'

import { useDimensions, Typography, Card, Button } from '@telavita-core/react-design-kit'
import { useSelector } from 'react-redux'
import clsx from 'clsx'

import { DISMISSED_PATIENT_REQUIRE_INFO_MODAL } from 'settings/_storageFlagsSettings'

import { RequiredPatientInfoModal, TranscriptionAuthorizerModal } from 'containers/ModalManager/Modals'
import MedicalEvaluationNotificationBanner from 'components/MedicalEvaluationNotificationBanner'
import { ReservationCard } from './ReservationCard/ReservationCard'
import { CreditsCard } from 'components/CreditsCard/CreditsCard'
import { ScheduleButtonOnboarding } from './ScheduleButtonOnboarding'
import { SpecialitiesSelector } from 'components'
import Column from 'components/Column'
import Row from 'components/Row'
import { ReactComponent as MediaTestIcon } from 'static/svg/test-media-icon.svg'

import { usePatientEligibility } from 'hooks/usePatientEligibility'
import { usePatientEmotionalHealthEvaluation, useUpdateProfileSelectedPlansLocally, useEnablePsychiatry } from 'hooks'
import { useAudioTranscriptionEligibility } from 'hooks/useAudioTranscriptionEligibility'

import { NotificationBanner } from './NotificationBanner'
import { NextScheduleWrapper } from './NextScheduleWrapper'
import { TelavitaHealthSpaceCard } from './components/TelavitaHealthSpaceCard'

const PatientHome = () => {
  const { isMobile } = useDimensions()

  const { showNotification: showPatientEmotionalHealthEvaluationNotification } = usePatientEmotionalHealthEvaluation()

  const { username, person_general: { 
    cpf,
    gender,
    first_name: firstName,
    date_birth: dateBirth
  }} = useSelector(state => state.auth.userData)
  
  const { updateProfileSelectedPlansLocally } = useUpdateProfileSelectedPlansLocally()

  useEffect(() => {
    updateProfileSelectedPlansLocally()
  }, [])
  
  const {
    isEnterprise,
    isCorporate,
    isCareplus,
    isCassi,
    isCapitation,
    hasButton,
    setHasButton,
    planGroupName,
    creditsStatus,
    availableCredits,
    maxCredits,
    enableDetails,
    validDate,
    expired,
    errorButton,
    loadingButton,
    solicitationDate,
    isUnlimited,
    startDate,
    corporateEligibility,
    loading,
    forceUpdate,
    psychiatryEligibility,
    psychiatristEligibilityLoading,
    cardNumber,
    planGroupCode,
    patientPlans,
    onClickRequestCreditsButton,
    onForceUpdate,
    handleRequestTreatementStatus,
    handleUpdateSelectedPlan,
    reasonCode,
    planCode,
    creditsPanelErrorMessage,
  } = usePatientEligibility()
  const [showRequiredPatientInfoModal, setShowRequiredPatientInfoModal] = useState(false)
  
  const showPsychiatry = useEnablePsychiatry()
  const { showFeature: showAudioTranscription } = useAudioTranscriptionEligibility()

  useEffect(() => {
    const showRequiredPacientInfoModal = sessionStorage.getItem(DISMISSED_PATIENT_REQUIRE_INFO_MODAL)
    const patientAlreadyHasRequiredInfo = cpf && dateBirth && gender
  
    if (!patientAlreadyHasRequiredInfo && !showRequiredPacientInfoModal) {
      setShowRequiredPatientInfoModal(true)
    }

  }, [cpf, dateBirth, gender])

  const containerStyle = clsx({
    'PatientHome': true,
    'PatientHome__mobile': isMobile
  })

  const renderCreditsCard = () =>
    <CreditsCard 
      isEnterprise={isEnterprise}
      isCorporate={isCorporate}
      isCareplus={isCareplus}
      isCassi={isCassi}
      isCapitation={isCapitation}
      showRequestTreatment={isEnterprise}
      hasButton={hasButton}
      setHasButton={setHasButton}
      planGroupName={planGroupName}
      creditsStatus={creditsStatus}
      availableCredits={availableCredits}
      maxCredits={maxCredits}
      enableDetails={enableDetails}
      validDate={validDate}
      expired={expired}
      errorButton={errorButton}
      loadingButton={loadingButton}
      solicitationDate={solicitationDate}
      patientPlans={patientPlans}
      isUnlimited={isUnlimited}
      startDate={startDate}
      corporateEligibility={corporateEligibility}
      loading={loading}
      onClickRequestCreditsButton={onClickRequestCreditsButton}
      forceUpdate={forceUpdate}
      psychiatryEligibility={psychiatryEligibility}
      psychiatryEligibilityLoading={psychiatristEligibilityLoading}
      onForceUpdate={onForceUpdate}
      onRequestTreatement={handleRequestTreatementStatus}
      cardNumber={cardNumber}
      planGroupCode={planGroupCode}
      reasonCode={reasonCode}
      handleUpdateSelectedPlan={handleUpdateSelectedPlan}
      showPlanSwitch={patientPlans.length > 2}
      planCode={planCode}
      showPsychiatry={showPsychiatry}
      creditsPanelErrorMessage={creditsPanelErrorMessage}
      username={username}
    />
  

  const renderMediaTestCard = () => (
    <Card 
      customClassName='ProfessionalHome__Card'
      inlineStyles={{
        display: 'flex',
        justifyContent: 'space-between',
      }}
    >
      <Row maxWidth='260px'>
        <Typography variant='content1' weight='bolder'>
            Testar câmera e microfone
        </Typography>
        <Typography
          variant='content3'
          inlineStyles={{
            marginTop: 5,
            color: '#898989',
            lineHeight: '16px',
          }}
        >
            Assegure-se de que vai funcionar no seu dispositivo.
        </Typography>
        <Button
          onClick={() => window.open(process.env.REACT_APP_MEET_TEST_URL, '_self')}
          variant='text'
          uppercase={false}
          style={{
            margin: 0,
            padding: 0,
            height: 'fit-content',
            marginTop: '30px',
          }}
        >
            Fazer um teste
        </Button>
      </Row>
      <MediaTestIcon />
    </Card>
  )

  const renderContent = () => {
    if (!isMobile)
      return (
        <div className='PatientHome__CardsWrapper'>
          <Column colWidth={6}>
            <NextScheduleWrapper />
            <ReservationCard />
            {renderMediaTestCard()}
          </Column>
          <Column colWidth={6}>
            {renderCreditsCard()}
            <TelavitaHealthSpaceCard />
          </Column>
        </div>
      )

    return (
      <Column colWidth={12}>
        <NextScheduleWrapper />
        {renderCreditsCard()}
        <ReservationCard />
        <TelavitaHealthSpaceCard />
        {renderMediaTestCard()}
      </Column>
    )
  }

  const handleCloseRequiredPatientInfoModal = () => {
    setShowRequiredPatientInfoModal(false)
    sessionStorage.setItem(DISMISSED_PATIENT_REQUIRE_INFO_MODAL, true)
  }

  const shouldShowScheduleButtonOnboarding = !loading && !showRequiredPatientInfoModal

  return (
    <>
      <div className={containerStyle}>
        {shouldShowScheduleButtonOnboarding && <ScheduleButtonOnboarding />}

        <Row className="Dashboard__Header">
          <Typography variant={!isMobile ? 'header2' : 'header4'}>
            Bem vindo(a), {firstName}
          </Typography>
          <SpecialitiesSelector />
        </Row>
        {showPatientEmotionalHealthEvaluationNotification && <MedicalEvaluationNotificationBanner />}
        <NotificationBanner />
        {renderContent()}
        
        {showAudioTranscription && (
          <TranscriptionAuthorizerModal
            firstName={firstName}
          />
        )}

        {showRequiredPatientInfoModal 
          ? (
            <RequiredPatientInfoModal 
              onClose={handleCloseRequiredPatientInfoModal}
              isFormFulfillmentObligatory={false}
              title="Complete seu cadastro"
              patientInfo={{
                cpf,
                dateBirth,
                gender
              }}
            />
          )
          : null
        }
      </div>
    </>
  )

}

export { PatientHome }
